import { message } from "ant-design-vue";
import store from "@/src/store/index.js";
import BillingService from "@/src/api/BillingService.js";
import mixpanel from "@/src/services/mixpanel/mixpanel.js";
import eventBus from "@/src/utils/eventBus.js";
import {
  billingPacks,
  billingSetupStatuses,
  billingStatusOptions,
  billingV3StatusOptions,
  billingV4StatusOptions,
} from "@/src/constants/billingConstants.js";
import { toTitleCase } from "@/src/utils/utils.js";

const getAppStoreVersion = () => {
  return window.APP_STORE_CONFIG?.version || "";
};

export const isBillingUnlimited = () => {
  try {
    return store.getters["billing/status"].toLowerCase() === billingStatusOptions.UNLIMITED.toLowerCase();
  } catch (error) {
    console.error(
      "An error occurred while checking if billing is unlimited: ",
      error,
    );
  }
};

export const isFreeTrial = () => {
  try {
    return store.getters["billing/status"].toLowerCase() === billingStatusOptions.FREE_TRIAL.toLowerCase() || store.getters["billing/status"].toLowerCase() === billingV3StatusOptions.TRIAL.toLowerCase();
  } catch (error) {
    console.error(
      "An error occurred while checking if its is free trial or not: ",
      error,
    );
  }
};

export const isFreePlan = () => {
  try {
    return store.getters["billing/status"].toLowerCase() === billingStatusOptions.FREE_PLAN.toLowerCase() || store.getters["billing/status"].toLowerCase() === billingV3StatusOptions.FREE.toLowerCase();
  } catch (error) {
    console.error(
      "An error occurred while checking if its is free plan or not: ",
      error,
    );
  }
};

export const isPaidPlan = () => {
  try {
    return store.getters["billing/status"].toLowerCase() === billingStatusOptions.PAID.toLowerCase() || store.getters["billing/status"].toLowerCase() === billingV3StatusOptions.ACTIVE.toLowerCase();
  } catch (error) {
    console.error(
      "An error occurred while checking if its is paid plan or not: ",
      error,
    );
  }
};

export const hasTrialExpired = () => {
  try {
    return store.getters["billing/status"].toLowerCase() === billingStatusOptions.FREE_PLAN.toLowerCase() || store.getters["billing/status"].toLowerCase() === billingV3StatusOptions.FREE.toLowerCase();
  } catch (error) {
    console.error(
      "An error occurred while checking if the trial period as expired or not: ",
      error,
    );
  }
};

export const checkBillingSetupComplete = () => {
  try {
    return store.getters["billing/isBillingSetupComplete"];
  } catch (error) {
    console.error(
      "An error occurred while checking is billing setup is complete or not: ",
      error,
    );
  }
};

export const toCalculateDue = async () => {
  try {
    const params = {
      plan_id: store.getters["billing/currentPlanID"],
      human_seats: store.getters["billing/userSeatCount"],
      digital_seats: store.getters["billing/digitalSeatCount"],
      billing_period: store.getters["billing/billingPack"],
    };

    await BillingService.calculateDue(params);
  } catch (error) {
    console.error("An error occurred while calculating due: ", error);
  }
};

const completeSetupBilling = async (mes, router) => {
  try {
    // Optimistic flip so the router guard doesn't bounce the user back to the
    // setup screen if the backend hasn't yet flipped status NEW → PAID. The
    // payment provider already confirmed success, so trust that.
    await store.dispatch("billing/setIsBillingSetupComplete", true);
    try {
      await store.dispatch("billing/getBillingDetails");
    } catch (e) { /* keep optimistic flag */ }

    const appStoreVersion = getAppStoreVersion();
    const redirectTo = appStoreVersion === "v2" ? "i-assistant" : "get_started";
    await router.push({ name: redirectTo });
    message.success(mes);
    eventBus.emit("billing-setup-complete");
  } catch (error) {
    console.error("An error occurred while completing setup billing: ", error);
  }
};

export const movingOutOfSetupBilling = async (mes, router) => {
  try {
    const response = await BillingService.updatePopUpShown();
    if (response?.success) {
      await completeSetupBilling(mes, router);
    }
  } catch (error) {
    console.error(
      "An error occurred while moving out of setup billing: ",
      error,
    );
  }
};

export const handlePaymentSuccess = async (mes, router) => {
  try {
    await completeSetupBilling(mes, router);
  } catch (error) {
    console.error("An error occurred while handling payment success: ", error);
  }
};

export const billingPackText = () => {
  try {
    return store.getters["billing/billingPack"] === billingPacks.MONTHLY
      ? "month"
      : "year";
  } catch (error) {
    console.error(
      "An error occurred while computing billing pack text: ",
      error,
    );
  }
};

// Maps frontend billing period values to backend API values
export const mapBillingPeriodToBackend = (frontendPeriod) => {
  const periodMap = {
    monthly: 'monthly',
    quarterly: 'quarterly',
    semi_annual: 'semi_annual',
    annual: 'annual',
    two_year: 'two_year',
  };
  return periodMap[frontendPeriod] || 'monthly';
};

export const handleFreePlan = async (router) => {
  try {
    const response = await BillingService.newAccountToFreePlan({});

    if (response?.success) {
      await handleBillingMixpanelEvent("Billing CTA Clicked", {
        Plan: "Free Plan",
      });
      await movingOutOfSetupBilling("Your free plan begins!", router);
    }
    return { success: response?.success };
  } catch (error) {
    console.error("Error activating free plan:", error);
    message.error("An error occurred while activating the free plan");
    return { success: false };
  }
};

export const handleTrialPlan = async (
  router,
  planTier = "growth",
  billingPeriod = "monthly",
) => {
  try {
    const baseUrl = window.location.origin;
    const appStoreVersion = getAppStoreVersion();
    const redirectPath = appStoreVersion === "v2" ? "/i-assistant" : "/get_started";
    const returnUrl = `${baseUrl}${redirectPath}`;

    // Map frontend billing period to backend expected value
    const backendBillingPeriod = mapBillingPeriodToBackend(billingPeriod);

    // Get plan_id from store (set from backend response), fallback to 1 if not available
    const planId = store.getters["billing/currentPlanId"] || 1;

    const payload = {
      plan_id: planId,
      billing_period: backendBillingPeriod,
      plan_tier: planTier,
      ui_mode: "embedded",
      return_url: returnUrl,
    };

    const response = await BillingService.checkoutSessionTrial(payload);
    if (response?.success) {
      await handleTrialPlanStatus(billingSetupStatuses.INITIATED);

      if (response?.client_secret) {
        return { success: true, clientSecret: response.client_secret };
      } else if (response?.url) {
        window.location.href = response.url;
        return { success: true };
      } else {
        message.error(response?.message || "Failed to start trial");
        return { success: false };
      }
    } else {
      message.error(response?.message || "Failed to checkout session");
      return { success: false };
    }
  } catch (error) {
    console.error("Error starting trial:", error);
    message.error("An error occurred while starting the trial");
    return { success: false };
  }
};

export const handleTrialPlanStatus = async (status) => {
  try {
    await handleBillingMixpanelEvent(`Checkout ${toTitleCase(status)}`, {
      Source: "billing_setup_v2",
      Status: status,
      Plan: "Free Trial",
    });
  } catch (error) {
    console.error(
      "An error occurred while handling trial plan status: ",
      error,
    );
  }
};

export const handleBillingMixpanelEvent = async (name, properties = {}) => {
  try {
    mixpanel.track(name, {
      Source: "billing_setup_v2",
      ...properties,
    });
  } catch (error) {
    console.error(
      "An error occurred while handling billing mixpanel event: ",
      error,
    );
  }
};
